import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBell,
  faBellSlash,
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { loanStatusData } from "./chase-list-data";
import {
  autoPotFailed,
  canMute,
  inAutoPot,
  paymentDeclined,
  retryDue,
  rowActions,
  type ChaseAction,
} from "../../utils/chase-actions.constants";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useChaseMenu } from "../../store/use-chase-menu";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import EditChaseModel from "../../models/chase-edit-model";
import { useChaseEditModel } from "../../store/use-chase-edit-model";
import moment from "moment";
import { IChase } from "../../types/chase-type";
import { money } from "../../utils/create-chase.constants";
import ChaseDropMenu from "./chase-drop-menu";
import useChaseAction from "../../utils/use-chase-action";

interface TChaseRenderitem {
  item: IChase;
  index: number;
}

const menuItemClass =
  "flex gap-2 px-4 py-2 text-xs text-light-gray items-center font-medium cursor-pointer hover:bg-overlay transition-colors duration-150";

/** Fixed-position panel pinned under the button; follows scroll, flips up near the bottom. */
const MenuPanel: React.FC<{
  anchor: HTMLElement;
  close: () => void;
  children: React.ReactNode;
}> = ({ anchor, close, children }) => {
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({});

  // Outside click / Escape — Menu only wires those up through <Menu.Items>.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panel.current?.contains(target) && !anchor.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, close]);

  useEffect(() => {
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const room = window.innerHeight - r.bottom > 220;
      setPos({
        position: "fixed",
        right: window.innerWidth - r.right,
        ...(room
          ? { top: r.bottom + 6 }
          : { bottom: window.innerHeight - r.top + 6 }),
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  return createPortal(
    <div ref={panel} style={pos} className="z-[99999] animate-fade-in">
      <div className="py-1 text-light-gray w-[auto] min-w-[150px] rounded-xl bg-surface-elevated border border-edge shadow-overlay">
        {children}
      </div>
    </div>,
    document.body
  );
};

/**
 * Row action menu. The panel is portalled to <body> — the chase table sits in an
 * overflow-x-auto wrapper, which clips anything positioned inside the row.
 *
 * Deliberately NOT Headless UI's <Menu>: its root runs useOutsideClick against
 * [buttonRef, itemsRef], and without a <Menu.Items> (this menu has none) a
 * portalled panel reads as "outside" — the capture-phase click listener then
 * preventDefault()s and closes the menu before any item's onClick can fire.
 */
const RowActionsMenu: React.FC<{
  children: (close: () => void) => React.ReactNode;
}> = ({ children }) => {
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Chase actions"
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="text-[15px]" />
      </button>

      {open && button.current && (
        <MenuPanel anchor={button.current} close={close}>
          {children(close)}
        </MenuPanel>
      )}
    </>
  );
};

const getStatus = (status: string) => {
  const statusData = loanStatusData.find(
    (stat) => stat.name === (status || "default")
  );
  return statusData;
};

const ChaseRenderitem: React.FC<TChaseRenderitem> = ({
  item: itemData,
  index,
}) => {
  const [item, setItem] = useState<IChase | null>(itemData);
  const [loading, setLoading] = useState<boolean>(false);
  const { initialValues, onToggleChaseMenu, onChaseMenuOpen } = useChaseMenu();
  const { initialValues: editInitialValues, onOpen: onEditOpen } =
    useChaseEditModel();

  // --------------------------- [ MUTE / UNMUTE CHASE ] ------------------------------
  const HandleToggleMute = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch(`/chase/${item?.id}/mute`);
      setItem((s) => (s ? { ...s, mute: res.data?.mute } : s));
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
    }
    setLoading(false);
  };

  // --------------------------- [ HANDLE ON EDIT CALL ] ------------------------------

  const HandleEditChase = () => {
    onEditOpen({ id: item?.id, data: item });
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const UpdateItemData = async () => {
    try {
      const res = await axiosInstance.get(`/chase?id=${item?.id}`);
      const resData = res.data?.data?.[0];
      // Archiving drops the chase out of this list — the row simply goes away.
      if (!resData) return setItem(null);
      onChaseMenuOpen({
        id: resData?.id,
        chase: resData,
      });
      setItem(resData);
    } catch (error) {
      console.log(error);
    }
  };

  const { runAction } = useChaseAction(
    item?.id,
    UpdateItemData,
    item?.versionReferenceNumber
  );

  // ------------------------------ [ RUN A ⋮ MENU ACTION ] ------------------------------
  // Edit opens the modal and mute has its own toggle route; the rest are one
  // confirmed PATCH each, which useChaseAction handles.

  const HandleAction = (action: ChaseAction) => {
    if (action.key === "edit") return HandleEditChase();
    if (action.key === "mute") return HandleToggleMute();
    return runAction(action);
  };

  if (!item) {
    return null;
  }

  return (
    <>
      <tr
        className="relative font-medium border-b border-edge-faint transition-colors duration-200 hover:bg-overlay-hover"
        key={index}
      >
        <td className="w-[20%] py-3.5 pl-1 align-top">
          {/* Wraps: the badges are shrink-0, so on a narrow name column they
              drop to their own line instead of running into the email cell. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="text-[14px] font-semibold text-text-primary">
              {item?.forename + " " + item.surname}
            </p>
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-light-gray border border-edge rounded-full px-2 py-[3px]">
              {item?.auto ? "Auto" : "Non-Auto"}
            </span>
            {/* Waiting on an agent to say whether the auto payment collected. */}
            {inAutoPot(item) ? (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-amber-600 dark:text-amber-400 border border-amber-400/50 rounded-full px-2 py-[3px]">
                Auto Payment Attempt
              </span>
            ) : null}
            {/* Was potted, payment failed — lit on every other day's retry. */}
            {autoPotFailed(item) ? (
              <span
                className={clsx(
                  "shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] border rounded-full px-2 py-[3px]",
                  retryDue(item)
                    ? "text-amber-600 dark:text-amber-400 border-amber-400/50 bg-amber-500/10"
                    : "text-light-gray border-edge"
                )}
              >
                {retryDue(item) ? "Retry" : "Auto Attempt"}
              </span>
            ) : null}
            {/* Still being chased, but a renewal payment has already been tried. */}
            {paymentDeclined(item) ? (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-rose-600 dark:text-rose-400 border border-rose-400/50 bg-rose-500/10 rounded-full px-2 py-[3px]">
                Payment Declined
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.companyName ? `${item.companyName} · ` : ""}
            {item?.policyReferenceNumber}
          </p>
        </td>
        <td className="py-3.5 w-[21%] align-top">
          <p className="text-[13px] text-text-primary break-all">
            {item?.email}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray tabular-nums">
            {item?.telephone}
          </p>
        </td>
        <td className="py-3.5 w-[14%] align-top">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {moment(item?.startDate).format("DD MMM YYYY")}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray whitespace-nowrap">
            Renews {moment(item?.renewalDate).format("DD MMM YYYY")}
          </p>
        </td>
        <td className="py-3.5 w-[10%] align-center text-center">
          {item?.quote?.premium ? (
            <p className="text-[14px] font-bold text-text-primary tabular-nums whitespace-nowrap">
              {money(Number(item.quote.premium))}
            </p>
          ) : (
            <p className="text-[13px] text-light-gray">Awaiting</p>
          )}
        </td>
        <td className="py-3.5 w-[11%] align-center text-center">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {item?.paymentType === "instalments"
              ? `Instalments ×${item?.instalments ?? ""}`.trim()
              : "Cash"}
          </p>
        </td>

        <td className="py-3.5 w-[12%] align-center">
          <div
            className={clsx(
              "inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full",
              getStatus(item?.status)?.bgColor
            )}
          >
            <FontAwesomeIcon
              icon={(getStatus(item?.status)?.icon ?? faPaperPlane) as IconProp}
              className={clsx("text-[11px]", getStatus(item?.status)?.color)}
            />
            <p
              className={clsx(
                "text-[11px] font-semibold whitespace-nowrap",
                getStatus(item?.status)?.color
              )}
            >
              {getStatus(item?.status)?.name?.replace("-", " ")}
            </p>
          </div>
        </td>
        {/* Mute — a muted chase keeps running but sends no email or SMS. */}
        <td className="py-3.5 align-center">
          <button
            onClick={HandleToggleMute}
            disabled={loading || !canMute(item)}
            title={
              canMute(item)
                ? item?.mute
                  ? "Unmute chase"
                  : "Mute chase"
                : `Muting doesn't apply to a ${item?.status} chase`
            }
            className={clsx(
              "cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center hover:bg-overlay transition-colors duration-200 disabled:opacity-[0.5] disabled:cursor-not-allowed",
              item?.mute
                ? "text-amber-500"
                : "text-light-gray hover:text-text-primary"
            )}
          >
            <FontAwesomeIcon
              icon={item?.mute ? faBellSlash : faBell}
              className="text-[15px]"
            />
          </button>
        </td>
        <td className="px-2 py-3.5 align-center">
          <RowActionsMenu>
            {(close) =>
              rowActions(item).map((action) => (
                <div
                  key={action.key}
                  role="menuitem"
                  className={menuItemClass}
                  onClick={() => {
                    close();
                    HandleAction(action);
                  }}
                >
                  <FontAwesomeIcon icon={action.icon} className="text-xs" />
                  {action.label}
                </div>
              ))
            }
          </RowActionsMenu>
        </td>
        <td className="py-3.5 align-center">
          <button
            onClick={() =>
              onToggleChaseMenu({
                id: item?.id,
                chase: item,
              })
            }
            className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-all duration-200 disabled:opacity-[0.5] disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon
              icon={
                initialValues?.id === item?.id ? faChevronUp : faChevronDown
              }
              className="text-[13px]"
            />
          </button>
        </td>
      </tr>
      <tr>
        {initialValues?.id === item?.id && (
          <ChaseDropMenu UpdateItemData={UpdateItemData} />
        )}
      </tr>

      {editInitialValues.id === item?.id && (
        <EditChaseModel UpdateItemData={UpdateItemData} />
      )}
    </>
  );
};

export default ChaseRenderitem;
